<template>
    <b-modal
        ref="dialog"
        title-tag="h2"
        :lazy="true"
        @hidden="close"
        title="Email Certificate"
        no-enforce-focus
    >
        <b-alert :show="showAlert" variant="warning">
            <i class="cus-error"></i>
            Generated certificates will be stored in patient Documents
        </b-alert>
        <b-form-group label-cols="3" label="Type:" label-for="type">
            <b-select v-model="type">
                <option value="0">Rabies</option>
                <option value="1">Reminders</option>
                <option value="2">Vaccination</option>
            </b-select>
        </b-form-group>

        <b-form-group
            label-cols="3"
            label="Reminders:"
            label-for="reminders"
            v-if="type !== '1'"
        >
            <b-select v-model="reminders">
                <option value="0">Do not include reminders</option>
                <option value="1">Include reminders</option>
            </b-select>
        </b-form-group>

        <b-form-group
            label-cols="3"
            label="Veterinarian:"
            label-for="veterinarian"
            v-if="type === '2'"
        >
            <b-select
                v-model="veterinarian"
                :options="veterinarianOptions"
                class="form-control"
            ></b-select>
        </b-form-group>

        <b-form-group label-cols="3" label="To:" label-for="to">
            <!-- do not change the module of vet select: logic handling issue -->
            <recipient-email
                v-validate="'required'"
                :class="{ 'border-danger': errors.has('recipients') }"
                name="recipients"
                v-model="recipients"
                multiple
                placeholder="Client or Contact Email (required)"
                @input="values => handleRecipientChangeAlt(values, true)"
                :key="recipientComponentKey"
                :client="clientDetails"
                :filterData="false"
            />
        </b-form-group>

        <b-form-group label-cols="3" label="Subject:" label-for="subject">
            <b-input
                v-model="subject"
                :disabled="isLoadingTemplate"
                :placeholder="
                    isLoadingTemplate ? 'Loading...' : 'Subject (optional)'
                "
            />
        </b-form-group>

        <b-form-group label-cols="3" label="Message:" label-for="message">
            <tinymce
                :init="tinymceConfig"
                v-model="message"
                @onKeyPress="contentChanged"
            />
        </b-form-group>

        <template v-slot:modal-footer>
            <loader v-if="isSending" :margin="1" />

            <b-button
                class="mr-auto"
                variant="secondary"
                @click="close"
                v-if="!isSending"
            >
                Cancel
            </b-button>

            <b-button v-if="!isSending" variant="primary" @click="send">
                Send
            </b-button>
        </template>
    </b-modal>
</template>

<script>
import Loader from "@/module/commons/loader";
import axios from "axios";
import RecipientEmail from "@/components/commons/recipients-email";
import tinymce from "@tinymce/tinymce-vue";
import tinymceHelper from "@/module/commons/helper-tinymce"; // helper
import EmailMixins from "@/mixins/email";

export default {
    components: {
        Loader,
        tinymce,
        RecipientEmail
    },
    mixins: [EmailMixins],
    props: {
        patientDetails: {
            type: Object
        }
    },
    data() {
        return {
            isSending: false,
            type: "0",
            reminders: "0",
            recipients: [],
            subject: "",
            isLoadingTemplate: true,
            message: "",
            defaultMessage: "",
            recipientComponentKey: 0,
            clientDetails: null,
            firstName: "",
            lastName: "",
            tinymceConfig: {
                ...tinymceHelper.core, // use spread operator to inject all vetter-related configs
                toolbar: false,
                plugins: tinymceHelper.plugins
            },
            veterinarian: "",
            veterinarianOptions: [],
            isContentChanged: false,
            showAlert: true,
            emailDefaultLetter: {
                body: null,
                subject: null
            }
        };
    },
    watch: {
        type(value) {
            if (value != 1) {
                this.showAlert = true;
            } else {
                this.showAlert = false;
            }
        }
    },
    computed: {
        owner() {
            // client aka owner
            const owners = this.patientDetails.primary_owner;
            return owners[0] || this._setting.clinic_default_owner || null;
        }
    },
    methods: {
        contentChanged() {
            // detect content has been changed by user (once)
            if (!this.isContentChanged) {
                this.isContentChanged = true;
            }
            return true;
        },
        async open() {
            this.$refs.dialog.show();
            await this.fetchTemplate(this.owner ? this.owner.id : "");
            this.clientDetails = this.owner;
            this.recipientComponentKey++;
            this.fetchVeterinarians();
        },
        close() {
            this.$refs.dialog.hide();
        },
        async fetchVeterinarians() {
            try {
                const res = await axios.get(
                    `${process.env.BARRAMUNDI}actor/staff/business/providers`
                );

                const response = res.data.response;

                if (response && response.resource && response.resource.data) {
                    let options = this.properVetList(response.resource.data);
                    this.veterinarianOptions = options;

                    let currentActor = this.$root.currentActor.id;
                    let isProvider = options.find(
                        opt => opt.value === currentActor
                    );

                    // set default vet to current actor
                    if (isProvider) {
                        this.veterinarian = currentActor;
                    }
                    // if current actor is not a provider (ie not in the options)
                    // use the first option as default
                    else {
                        this.veterinarian = options[0] ? options[0].value : "";
                    }
                } else {
                    throw new Error("Unable to fetch veterinarian list.");
                }
            } catch (e) {
                this._message(e.message, "danger");
            }
        },
        properVetList(rawData = []) {
            let sortedArr = _.sortBy(rawData, "actor.lastname");
            let result = [];

            for (const record of sortedArr) {
                result.push({
                    text: record.actor.label,
                    value: record.actor.id
                });
            }

            return result;
        },
        async send() {
            try {
                let valid = await this.$validator.validateAll();
                if (!valid) {
                    return;
                }

                let formData = new FormData();
                formData.append("id", this.patientDetails.id);
                formData.append("email_type", this.EMAIL_TYPE.CERTIFICATE);
                formData.append("first_name", this.firstName);
                formData.append("last_name", this.lastName);
                formData.append("type", this.type);
                formData.append("reminder-option", this.reminders);
                formData.append("veterinarian", this.veterinarian);
                formData.append("fax_subject", this.subject);
                formData.append("fax_note", "");
                formData.append("include_cover_letter", "true");
                formData.append("subject", this.subject);
                formData.append("message", this.message);
                formData.append("action", "email");
                formData.append(
                    "timestamp",
                    Math.floor(new Date().getTime() / 1000)
                );

                for (const rec of this.recipients) {
                    formData.append("email_to[]", rec.to_id);
                }

                this.isSending = true;

                const res = await axios.post(
                    `${process.env.CI_APPS}patient/record/export`,
                    formData
                );

                if (Number(res.data.state) === 0) {
                    this.isSending = false;
                    this._message(res.data.message);
                    this.$emit("onsuccess", { cert_type: this.type });
                    this.close();
                } else {
                    throw new Error(res.data.message);
                }
            } catch (e) {
                this.isSending = false;
                this._message(e.message, "danger");
            }
        },
        async handleRecipientChange(values = []) {
            /**
             * if user clears up recipient field, reload the default content
             * but only if: user never change the initial content
             */
            if (values.length === 0 && !this.isContentChanged) {
                this.fetchTemplate(null);
            }
            // replace variables
            else {
                this.handleRecipientChangeAlt(values);
            }
        },
        async fetchTemplate(recipientId) {
            try {
                const settings = await this._getSetting();
                let patientId = this.patientDetails.id;
                let primaryOwner = this.patientDetails.primary_owner;
                let owner;
                let isNoOwner = false;

                if (primaryOwner && primaryOwner[0]) {
                    owner = primaryOwner[0];
                } else {
                    owner = this._setting.clinic_default_owner;
                    isNoOwner = true;
                }

                const formData = new FormData();
                formData.append(
                    "params[0][email_type]",
                    this.EMAIL_TYPE.CERTIFICATE
                );
                formData.append("params[0][business_id]", settings.business_id);
                formData.append("params[0][patient_id][]", patientId);
                formData.append("params[0][recipient_id]", recipientId);
                formData.append("params[0][recipient_type]", "0");
                formData.append("params[0][skip_variable_replacement]", true);

                if (owner) {
                    formData.append("params[0][client_id]", owner.id);
                }

                if (isNoOwner) {
                    formData.append("params[0][use_clinic_default_owner]", 1);
                }

                this.isLoadingTemplate = true;

                const res = await axios.post(
                    `${process.env.CI_APPS}common/appointment/getEmailTemplate`,
                    formData
                );

                if (
                    res.data.response &&
                    res.data.response[0] &&
                    res.data.response[0].email_template
                ) {
                    this.isLoadingTemplate = false;

                    this.emailDefaultLetter = {
                        body: res.data.response[0].email_template.message,
                        subject: res.data.response[0].email_template.subject
                    };

                    this.subject = this.emailDefaultLetter.subject;
                    this.message = this.setNamesElement(
                        this.emailDefaultLetter.body,
                        false
                    );

                    // this.defaultMessage = message;
                } else {
                    throw new Error("Unable to fetch email content.");
                }
            } catch (e) {
                this.isLoadingTemplate = false;
                this._message(e.message, "danger");
            }
        }
    }
};
</script>
